import type { Ref, ReactNode, CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { MessageValue, NoMessageValues } from 'react-intl';

import { type FragmentType } from '@packages/gql/generated/shopping';
import type { FilterSettingsDesktopFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { unmask } from '@packages/gql/src/betterMasking';
import type { FilterSettingsDesktopFragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsDesktopFragmentFragmentDoc';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Popover, PopoverTrigger } from '@packages/shared/src/ui/Popover/Popover';
import { cn } from '@packages/shared/src/utils/cn/cn';

import { PRODUCT_LIST_ID } from '../../ids';
import type { SelectedFilterValue } from '../../types';
import { FilterSettingsDesktopButton } from '../FilterSettingsDesktopButton/FilterSettingsDesktopButton';
import { DynamicFilterSettingsDesktopPopover } from './FilterSettingsDesktopPopover.dynamic';
import { useFilterSettingsPopover } from './useFilterSettingsPopover';

import styles from './FilterSettingsDesktop.module.scss';

/* GraphQL */ `
  fragment FilterSettingsDesktopFragment on SearchFilterableAndSortableResult {
    id
    availableFilters {
      id
      displayName
      ...FilterSettingsDesktopPopoverFragment
    }
  }
`;

export const filterMessages = defineMessages<{
  readonly collapseFiltersLabel: NoMessageValues;
  readonly expandFiltersLabel: { readonly filterCount: MessageValue };
  readonly filtersProductList: NoMessageValues;
}>({
  collapseFiltersLabel: {
    defaultMessage: 'Weniger anzeigen',
    description: 'button label for the desktop filter settings collapse button',
    id: 'filterSettingsDesktop.collapseButtonLabel',
  },
  expandFiltersLabel: {
    defaultMessage: 'Mehr anzeigen ({filterCount})',
    description: 'button label for the desktop filter settings expand button',
    id: 'filterSettingsDesktop.expandButtonLabel',
  },
  filtersProductList: {
    id: 'filterSettings.productList.label',
    defaultMessage: 'Produktfilter',
  },
});

export type FilterSettingsDesktopProps = {
  maskedData: FragmentType<typeof FilterSettingsDesktopFragmentFragmentDoc>;
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (selectedValues: SelectedFilterValue[]) => void;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
};

type FilterItem = FilterSettingsDesktopFragmentFragment['availableFilters'][number];

type CollapseButtonProps = {
  children: ReactNode;
  iconComponent: ReactNode;
  ref?: Ref<HTMLButtonElement>;
  onClick?: () => void;
  className?: string;
  ariaLabel?: string;
};

const CollapseButton = ({
  children,
  iconComponent,
  ref,
  onClick,
  className,
  ariaLabel,
}: CollapseButtonProps) => (
  <Button
    ref={ref}
    className={cn('flex h-[2.25rem] w-full px-1', className)}
    onClick={onClick}
    aria-label={ariaLabel}
  >
    <div className="mr-auto w-full overflow-hidden text-left leading-normal hyphens-auto">
      {children}
    </div>
    {iconComponent}
  </Button>
);

const useCollapseTransition = () => {
  const [state, setState] = useState<'collapsed' | 'expanded'>('collapsed');

  const showMore = () => {
    setState('expanded');
  };

  const showLess = () => {
    setState('collapsed');
  };

  return {
    showingAllFilters: state !== 'collapsed',
    showMore,
    showLess,
  };
};

/**
 * FilterSettingsDesktop component description displayed in storybook
 * */
export const FilterSettingsDesktop = ({
  maskedData,
  onSubmit,
  selectedFilterValues: initiallySelectedFilterValues,
}: FilterSettingsDesktopProps) => {
  const gridRef = useRef<HTMLDivElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const [openFilterId, setOpenFilterId] = useState<string | null>(null);

  const { showingAllFilters, showMore, showLess } = useCollapseTransition();

  const data = unmask<typeof FilterSettingsDesktopFragmentFragmentDoc>(maskedData);
  const totalFilters = data.availableFilters.length;
  const [visibleLimit, setVisibleLimit] = useState(9);

  // The limit changes with the breakpoint, and the grid spans the viewport width, so a resize of the grid is when to read it.
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;

    const observer = new ResizeObserver(() => {
      const limit = parseInt(getComputedStyle(grid).getPropertyValue('--visible-limit'), 10);
      if (!Number.isNaN(limit)) setVisibleLimit(limit);
    });
    observer.observe(grid);

    return () => observer.disconnect();
  }, []);

  const hiddenFilterCount = Math.max(0, totalFilters - visibleLimit);

  const handleShowMore = useCallback(() => {
    showMore();
    // using requestAnimationFrame to ensure that the button is focused after the collapse animation has finished
    requestAnimationFrame(() => {
      if (!gridRef.current) return;

      const buttons = gridRef.current.querySelectorAll<HTMLElement>('[data-filter-button]');
      buttons[visibleLimit]?.focus();
    });
  }, [visibleLimit, showMore]);

  const handleShowLess = useCallback(() => {
    showLess();
    requestAnimationFrame(() => {
      expandButtonRef.current?.focus();
    });
  }, [showLess]);

  const {
    activeFilterIds,
    selectedFilterId,
    isPopoverOpen,
    pendingFilterValues,
    hasChanges,
    selectFilter,
    deselectFilter,
    closePopover,
    toggleFilterValue,
    setFilterRange,
  } = useFilterSettingsPopover(initiallySelectedFilterValues);

  const selectedFilter = data.availableFilters.find((filter) => filter.id === selectedFilterId);
  const { formatMessage } = useIntl();

  const renderFilterButton = (filter: FilterItem) => (
    <div key={filter.id} className={styles.c_filter}>
      <Popover
        open={openFilterId === filter.id}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            selectFilter(filter.id);
            setOpenFilterId(filter.id);
            return;
          }
          // Radix dismisses the previous popover only after the next one has opened, so closing must not clear another filter.
          deselectFilter(filter.id);
          setOpenFilterId((current) => (current === filter.id ? null : current));
        }}
      >
        <PopoverTrigger asChild>
          <FilterSettingsDesktopButton
            isActive={activeFilterIds.includes(filter.id)}
            isPopoverOpen={selectedFilterId === filter.id && isPopoverOpen}
            onClick={() => {
              selectFilter(filter.id);
            }}
          >
            {filter.displayName}
          </FilterSettingsDesktopButton>
        </PopoverTrigger>
        <DynamicFilterSettingsDesktopPopover
          requestCacheKey={data.id}
          maskedData={selectedFilter}
          pendingFilterValues={pendingFilterValues}
          hasChanges={hasChanges}
          closePopover={() => {
            closePopover();
            setOpenFilterId(null);
          }}
          onToggle={toggleFilterValue}
          onSetRange={setFilterRange}
          onSubmit={(values) => {
            onSubmit?.(values);
            setOpenFilterId(null);
          }}
          onCloseAutoFocus={(event) => {
            // Focus returning to this trigger would land outside the popover that just opened and dismiss it.
            if (openFilterId !== null) event.preventDefault();
          }}
        />
      </Popover>
    </div>
  );

  return (
    <nav
      id="product-filters"
      aria-label={formatMessage(filterMessages.filtersProductList)}
      aria-controls={PRODUCT_LIST_ID}
    >
      <div
        ref={gridRef}
        className={cn('grid grid-cols-5 gap-2 md:max-lg:grid-cols-4', styles.c_grid)}
        data-expanded={showingAllFilters}
        style={
          {
            '--total-filters': totalFilters,
          } as CSSProperties
        }
      >
        {data.availableFilters.map((filter) => renderFilterButton(filter))}

        <CollapseButton
          ref={expandButtonRef}
          className={styles.c_expandButton}
          iconComponent={<span className="icon-emp-plus size-4" />}
          onClick={handleShowMore}
          ariaLabel={formatMessage(filterMessages.expandFiltersLabel, {
            filterCount: hiddenFilterCount,
          })}
        >
          <FormattedMessage
            key="expandFiltersLabel-text"
            {...filterMessages.expandFiltersLabel}
            values={{
              filterCount: <span className={styles.c_additionalCount} />,
            }}
          />
        </CollapseButton>

        <CollapseButton
          className={styles.c_collapseButton}
          iconComponent={<span className="icon-emp-minus size-4" />}
          onClick={handleShowLess}
          ariaLabel={formatMessage(filterMessages.collapseFiltersLabel)}
        >
          <FormattedMessage
            key="collapseFiltersLabel-text"
            {...filterMessages.collapseFiltersLabel}
          />
        </CollapseButton>
      </div>
    </nav>
  );
};
